import {handleUnauthorized} from "services/http/authInterceptor";
import createHash from "services/http/createHash";
import {
  ApiError,
  type RequestConfig,
  type unknownRecord,
} from "services/http/types";
import {
  BODY_METHODS,
  createCompleteURL,
  isFormData,
  pendingRequests,
  replacePathParams,
} from "services/http/utils";
import {getSessionStorageItem} from "utils/storage";

/**
 * createRequestHandler
 *
 * Accepts Args that may be:
 *  - an object (unknownRecord) for path/query/body
 *  - a FormData instance (used as body for POST/PUT/PATCH)
 *  - undefined/void
 */
const createRequestHandler =
  <Res, Args = unknownRecord | FormData | void>(config: RequestConfig) =>
  (params: Args) => {
    const hash = createHash({
      method: config.method,
      path: config.path,
      params,
    });
    const isMutation = config.method !== "GET";

    if (!isMutation) {
      const ongoingRequest = pendingRequests.get(hash);
      if (ongoingRequest) return ongoingRequest as Promise<any>;
    }

    let path: string;
    let httpParams: unknownRecord | FormData | undefined;

    if (isFormData(params)) {
      path = config.path;
      httpParams = params;
    } else {
      const [p, otherArgs] = replacePathParams(
        config.path,
        (params ?? {}) as unknownRecord,
      );
      path = p;
      httpParams = otherArgs;
    }

    const queryParams = BODY_METHODS.has(config.method)
      ? config.queryParams
      : (httpParams as unknownRecord | undefined);

    const url = createCompleteURL(path, queryParams);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    try {
      const stored = getSessionStorageItem<{token?: string}>("authUser");
      const token = stored?.token;
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch {
      // ignore storage read errors
    }

    let body: BodyInit | undefined;
    if (BODY_METHODS.has(config.method)) {
      if (isFormData(httpParams)) {
        body = httpParams as FormData;
        delete headers["Content-Type"];
      } else if (httpParams !== undefined) {
        body = JSON.stringify(httpParams);
      }
    }

    const pendingRequest = fetch(url, {
      method: config.method,
      credentials: "include",
      cache: "no-cache",
      redirect: "follow",
      headers,
      body,
    })
      .catch(() => {
        throw new ApiError(500);
      })
      .then(async res => {
        const headersObj: Record<string, string> = {};
        try {
          res.headers.forEach((value, key) => {
            headersObj[key] = value;
          });
        } catch {
          // ignore header iteration errors
        }

        if (res.ok) {
          const contentType = res.headers.get("Content-Type") || "";
          let parsed: any;

          if (contentType.includes("application/json")) {
            parsed = await res.json().catch(() => undefined);
          } else {
            // Treat non-JSON as text (CSV, plain text, etc.)
            parsed = await res.text().catch(() => undefined);
          }

          return {payload: parsed as Res, headers: headersObj};
        }

        const errorData = await res
          .json()
          .then(data => ("errors" in data ? data.errors : data))
          .catch(() => undefined);

        if (res.status === 401 && errorData?.message === "Invalid Token") {
          handleUnauthorized();
        }

        throw new ApiError(
          res.status,
          errorData?.message || res.statusText,
          errorData,
        );
      })
      .finally(() => {
        pendingRequests.delete(hash);
      });

    pendingRequests.set(hash, pendingRequest);
    return pendingRequest;
  };

export default createRequestHandler;
